import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import '../../styles/MasterDataManagement.css';
const FULLY_PROTECTED_TABS = new Set(['demoStatuses']);

/*
 * Tabs whose rows support hide_from_user / display_order edits
 * via the system-safe endpoint. Mirrors SYSTEM_AWARE_TYPES on
 * the backend.
 */
const SYSTEM_SAFE_TABS = new Set([
  'leadStatuses',
  'leadTypes',
  'activityTypes',
  'demoStatuses',
]);

function MasterDataManagement() {
  const [activeTab, setActiveTab] = useState('leadStatuses');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    displayOrder: 0,
    isActive: 1,
  });

  // Modal state — showing a system value
  const [confirmShow, setConfirmShow] = useState(null);
  // { id, name, type, onConfirm }

  // Inline display-order edit state
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [orderDraft, setOrderDraft] = useState('');

  const [busyRowId, setBusyRowId] = useState(null);
  const [rowError, setRowError] = useState('');

  const tabs = [
    { id: 'leadSources',       label: 'Lead Sources' },
    { id: 'leadTypes',         label: 'Lead Types' },
    { id: 'serviceDashboards', label: 'Service Dashboards' },
    { id: 'leadStatuses',      label: 'Legacy Lead Statuses' },
    { id: 'demoStatuses',      label: 'Demo Statuses' },
    { id: 'activityTypes',     label: 'Activity Types' },
    { id: 'manufacturers',     label: 'Manufacturers' },
  ];

  const activeTabIsFullyProtected = FULLY_PROTECTED_TABS.has(activeTab);
  const activeTabSupportsSystemSafe = SYSTEM_SAFE_TABS.has(activeTab);
  const canCreate = !activeTabIsFullyProtected;

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await api.getMasterData(activeTab);
      setData(response || []);
    } catch (err) {
      console.error('Failed to load data:', err);
      alert('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.updateMasterData(activeTab, editing.id, formData);
      } else {
        await api.createMasterData(activeTab, formData);
      }
      loadData();
      setShowAdd(false);
      setEditing(null);
      setFormData({ name: '', displayOrder: 0, isActive: 1 });
    } catch (err) {
      console.error('Failed to save:', err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Failed to save';
      alert(msg);
    }
  };

  const handleEdit = (item) => {
    if (item.isSystem) {
      // We never open the full edit form for system rows.
      return;
    }
    setEditing(item);
    setFormData({
      name: item.name,
      displayOrder: item.display_order || 0,
      isActive: item.is_active,
    });
    setShowAdd(true);
  };

  const handleDelete = async (id) => {
    const item = data.find((d) => d.id === id);
    if (item?.isSystem) {
      alert('This entry is system-managed and cannot be deleted.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this?')) return;
    try {
      await api.deleteMasterData(activeTab, id);
      loadData();
    } catch (err) {
      console.error('Failed to delete:', err);
      alert(err?.response?.data?.error || 'Failed to delete');
    }
  };

  /* ──────────────────────────────────────────────────────
     Hide toggle
     ────────────────────────────────────────────────────── */

  const handleToggleHide = (item) => {
    const nextHideState = !item.hideFromUser;

    if (nextHideState === true) {
      // Hiding is safe — apply immediately
      applySafeUpdate(item, { hideFromUser: true });
      return;
    }

    if (item.isSystem) {
      setConfirmShow({
        id: item.id,
        name: item.name,
        type: activeTab,
        onConfirm: () => {
          setConfirmShow(null);
          applySafeUpdate(item, { hideFromUser: false });
        },
      });
      return;
    }

    // Non-system row — apply immediately
    applySafeUpdate(item, { hideFromUser: false });
  };

  /* ──────────────────────────────────────────────────────
     Display order inline edit
     ────────────────────────────────────────────────────── */

  const startEditOrder = (item) => {
    setEditingOrderId(item.id);
    setOrderDraft(String(item.display_order ?? 0));
    setRowError('');
  };

  const cancelEditOrder = () => {
    setEditingOrderId(null);
    setOrderDraft('');
  };

  const commitEditOrder = async (item) => {
    const n = Number(orderDraft);
    if (!Number.isInteger(n) || n < 0) {
      setRowError('Display order must be a non-negative integer.');
      return;
    }
    if (n === (item.display_order ?? 0)) {
      cancelEditOrder();
      return;
    }
    await applySafeUpdate(item, { displayOrder: n });
    cancelEditOrder();
  };

  /* ──────────────────────────────────────────────────────
     The one write path for system-safe fields
     ────────────────────────────────────────────────────── */

  const applySafeUpdate = async (item, payload) => {
    setBusyRowId(item.id);
    setRowError('');
    try {
      const result = await api.systemSafeUpdateMasterData(
        activeTab,
        item.id,
        payload,
      );

      // Optimistic local update using the API's response
      setData((prev) =>
        prev.map((row) => {
          if (row.id !== item.id) return row;

          const next = { ...row };

          if (payload.hideFromUser !== undefined) {
            next.hideFromUser = !!payload.hideFromUser;
          }
          if (payload.displayOrder !== undefined) {
            next.display_order = payload.displayOrder;
          }

          return next;
        }),
      );
    } catch (err) {
      console.error('Failed to update system-safe fields:', err);
      setRowError(
        err?.response?.data?.error ||
          'Failed to update. Please try again.',
      );
    } finally {
      setBusyRowId(null);
    }
  };

  if (loading) return <LoadingSpinner message="Loading..." />;

  const hasAnySystemRow = data.some((item) => item.isSystem);

  return (
    <div className="master-data-management">
      <div className="management-header">
        <h2>Master Data Management</h2>
        {canCreate && (
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
            <i className="fas fa-plus"></i> Add New
          </button>
        )}
      </div>

      <div className="admin-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {(activeTabIsFullyProtected || hasAnySystemRow) && (
        <div
          style={{
            padding: '10px 14px',
            background: '#e7f1ff',
            border: '1px solid #b9d4fb',
            borderRadius: '6px',
            marginBottom: '12px',
            color: '#0b3d91',
            fontSize: '0.85rem',
          }}
        >
          <i className="fas fa-info-circle"></i>{' '}
          Locked rows are system-managed. You can change their display
          order and whether they appear in user forms. Rename and delete
          remain disabled.
        </div>
      )}

      {rowError && (
        <div className="error-message" style={{ marginBottom: 12 }}>
          <i className="fas fa-exclamation-circle"></i> {rowError}
        </div>
      )}

      {showAdd && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>
              {editing ? 'Edit' : 'Add New'}{' '}
              {tabs.find((t) => t.id === activeTab)?.label}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.displayOrder}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      displayOrder: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div className="form-group">
                <label>Active</label>
                <select
                  className="form-control"
                  value={formData.isActive}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      isActive: parseInt(e.target.value),
                    })
                  }
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAdd(false);
                    setEditing(null);
                    setFormData({ name: '', displayOrder: 0, isActive: 1 });
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Display Order</th>
              <th>Status</th>
              {activeTabSupportsSystemSafe && <th>In User Forms</th>}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr key={item.id}>
                <td>
                  <strong>{item.name}</strong>
                  {item.isSystem && (
                    <span
                      className="badge badge-primary"
                      title="System-managed"
                      style={{ marginLeft: 8, fontSize: '0.65rem' }}
                    >
                      <i className="fas fa-lock" /> System
                    </span>
                  )}
                </td>

                {/* Display Order cell — inline editable for system-safe tabs */}
                <td>
                  {activeTabSupportsSystemSafe &&
                  editingOrderId === item.id ? (
                    <div
                      style={{
                        display: 'flex',
                        gap: 6,
                        alignItems: 'center',
                      }}
                    >
                      <input
                        type="number"
                        min="0"
                        className="form-control form-control-sm"
                        style={{ width: 80 }}
                        value={orderDraft}
                        autoFocus
                        onChange={(e) => setOrderDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitEditOrder(item);
                          if (e.key === 'Escape') cancelEditOrder();
                        }}
                        disabled={busyRowId === item.id}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => commitEditOrder(item)}
                        disabled={busyRowId === item.id}
                        title="Save"
                      >
                        <i className="fas fa-check"></i>
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={cancelEditOrder}
                        disabled={busyRowId === item.id}
                        title="Cancel"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        gap: 6,
                        alignItems: 'center',
                      }}
                    >
                      <span>{item.display_order || 0}</span>
                      {activeTabSupportsSystemSafe && (
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost"
                          onClick={() => startEditOrder(item)}
                          disabled={busyRowId === item.id}
                          title="Edit display order"
                          style={{
                            padding: '2px 6px',
                            fontSize: '0.7rem',
                            color: '#6b7280',
                          }}
                        >
                          <i className="fas fa-pencil-alt"></i>
                        </button>
                      )}
                    </div>
                  )}
                </td>

                <td>
                  <span
                    className={`status-badge ${
                      item.is_active ? 'active' : 'inactive'
                    }`}
                  >
                    {item.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>

                {activeTabSupportsSystemSafe && (
                  <td>
                    <button
                      type="button"
                      className={`visibility-toggle ${
                        item.hideFromUser ? 'is-hidden' : 'is-visible'
                      }`}
                      onClick={() => handleToggleHide(item)}
                      disabled={busyRowId === item.id}
                      title={
                        item.hideFromUser
                          ? 'Hidden from user forms — click to show'
                          : 'Visible in user forms — click to hide'
                      }
                    >
                      {item.hideFromUser ? 'Hidden' : 'Visible'}
                    </button>
                  </td>
                )}

                <td className="actions">
                  {item.isSystem ? (
                    <span
                      className="text-muted"
                      title="System-managed. Rename and delete are locked."
                      style={{ fontSize: '0.8rem' }}
                    >
                      <i className="fas fa-lock"></i> System
                    </span>
                  ) : (
                    <>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEdit(item)}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(item.id)}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Warning modal — showing a system value */}
      {confirmShow && (
        <div
          className="modal-overlay"
          role="presentation"
          onMouseDown={() => setConfirmShow(null)}
        >
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-show-title"
            style={{ maxWidth: 520 }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="confirm-show-title">
                <i
                  className="fas fa-exclamation-triangle"
                  style={{ color: '#d97706', marginRight: 8 }}
                />
                Show this system value?
              </h3>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setConfirmShow(null)}
                aria-label="Close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p style={{ marginBottom: 12 }}>
                You are about to make{' '}
                <strong>&ldquo;{confirmShow.name}&rdquo;</strong> available in
                user forms for <strong>{confirmShow.type}</strong>.
              </p>

              <div
                style={{
                  background: '#fff7ed',
                  border: '1px solid #fed7aa',
                  borderRadius: 6,
                  padding: '12px 14px',
                  fontSize: '0.85rem',
                  color: '#7c2d12',
                  lineHeight: 1.5,
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  <i className="fas fa-info-circle"></i> What this affects
                </div>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  <li>
                    This value will appear in Lead Status, Lead Type, or
                    Activity Type dropdowns for all users.
                  </li>
                  <li>
                    If this value is written by the automated demo sync or
                    webhook pipeline, users may set it manually and the
                    pipeline may overwrite their choice on the next sync
                    (booking app always wins).
                  </li>
                  <li>
                    System protection stays in effect: rename and delete
                    remain locked.
                  </li>
                </ul>
              </div>

              <p
                style={{
                  marginTop: 14,
                  color: '#6b7280',
                  fontSize: '0.8rem',
                }}
              >
                You can hide it again at any time. Hiding does not require
                confirmation.
              </p>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setConfirmShow(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmShow.onConfirm}
              >
                <i className="fas fa-check"></i> Show in user forms
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MasterDataManagement;